import { useTranslation } from 'react-i18next';

const STAT_ICONS = ['ri-award-line', 'ri-checkbox-circle-line', 'ri-emotion-happy-line', 'ri-team-line'];
const WHY_ICONS = [
  'ri-shield-check-line', 'ri-time-line', 'ri-customer-service-2-line',
  'ri-map-pin-2-line', 'ri-building-2-line', 'ri-thumb-up-line',
];

const ABOUT_BG = "https://public.readdy.ai/ai/img_res/c3c1dc7647bf178a55a55ceb97b0bae4.jpg";

export default function About() {
  const { t } = useTranslation();

  const STATS = [
    { value: t('about.stat1.value'), label: t('about.stat1.label'), icon: STAT_ICONS[0] },
    { value: t('about.stat2.value'), label: t('about.stat2.label'), icon: STAT_ICONS[1] },
    { value: t('about.stat3.value'), label: t('about.stat3.label'), icon: STAT_ICONS[2] },
    { value: t('about.stat4.value'), label: t('about.stat4.label'), icon: STAT_ICONS[3] },
  ];

  const WHY_ITEMS = [1, 2, 3, 4, 5, 6].map((n, i) => ({
    icon: WHY_ICONS[i],
    title: t(`why.item${n}.title`),
    desc: t(`why.item${n}.desc`),
  }));

  return (
    <>
      {/* ── About Section ── */}
      <section id="about" className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-[#EAF5FF] via-[#F0F8FF] to-[#E5F2FC]">

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          {/* Section header */}
          <div className="mb-12 md:mb-16">
            <span className="text-[#0E6AAF] text-xs tracking-widest uppercase font-semibold">
              {t('about.label')}
            </span>
            <h3 className="text-3xl md:text-5xl font-bold text-[#05294A] mt-4 leading-tight">
              {t('about.heading')}{' '}
              <span className="text-[#0E82CC]">{t('about.headingEm')}</span>
            </h3>
            <div className="w-12 h-0.5 bg-[#0E82CC] mt-5" />
          </div>

          {/* Main grid: text left · stats right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">

            {/* ── Left: paragraphs + service highlights ── */}
            <div className="lg:col-span-3 flex flex-col gap-6">
              {/* Card for body text */}
              <div className="bg-white border border-[#C2DFF2] p-6 md:p-8 rounded-xl">
                <p
                  className="text-[#1A4A6E] text-base leading-relaxed mb-4"
                  dangerouslySetInnerHTML={{ __html: t('about.p1') }}
                />
                <p className="text-[#2A6A96] text-base leading-relaxed">
                  {t('about.p2')}
                </p>
              </div>

              {/* Service highlight cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 p-5 bg-white border border-[#C2DFF2] rounded-xl hover:border-[#67CBFF] hover:shadow-md transition-all duration-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0E6AAF] text-white rounded-lg shrink-0 mt-0.5">
                    <i className="ri-building-4-line text-base" />
                  </div>
                  <div>
                    <p className="text-[#05294A] font-bold text-sm">{t('about.svc1.title')}</p>
                    <p className="text-[#2A6A96] text-xs mt-1 leading-relaxed">{t('about.svc1.desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-white border border-[#C2DFF2] rounded-xl hover:border-[#67CBFF] hover:shadow-md transition-all duration-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0E6AAF] text-white rounded-lg shrink-0 mt-0.5">
                    <i className="ri-home-gear-line text-base" />
                  </div>
                  <div>
                    <p className="text-[#05294A] font-bold text-sm">{t('about.svc2.title')}</p>
                    <p className="text-[#2A6A96] text-xs mt-1 leading-relaxed">{t('about.svc2.desc')}</p>
                  </div>
                </div>
              </div>

              {/* Trust badge */}
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-[#0E6AAF]/10 border border-[#0E6AAF]/30 rounded-full w-fit">
                <div className="w-7 h-7 flex items-center justify-center bg-[#0E6AAF] text-white rounded-full">
                  <i className="ri-medal-line text-sm" />
                </div>
                <div>
                  <span className="text-[#05294A] font-bold text-sm">{t('about.badge.title')}</span>
                  <span className="text-[#2A6A96] text-xs ml-2">{t('about.badge.label')}</span>
                </div>
              </div>
            </div>

            {/* ── Right: stats grid ── */}
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {STATS.map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col items-start p-5 bg-white border border-[#C2DFF2] rounded-xl hover:border-[#67CBFF] hover:shadow-md transition-all duration-200"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#DDEEFF] text-[#0E6AAF] rounded-lg mb-3">
                    <i className={`${stat.icon} text-lg`} />
                  </div>
                  <p className="text-3xl font-bold text-[#05294A] leading-none">{stat.value}</p>
                  <p className="text-[#2A6A96] text-xs mt-1.5 leading-snug">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section id="why" className="py-16 md:py-24 bg-[#EAF5FF]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-[#0E6AAF] text-xs tracking-widest uppercase font-semibold">
              {t('why.label')}
            </span>
            <h3 className="text-3xl md:text-5xl font-bold text-[#05294A] mt-4">
              {t('why.heading')} <span className="text-[#0E82CC]">{t('why.headingEm')}</span>
            </h3>
            <p className="text-[#2A6A96] mt-4 max-w-lg mx-auto text-base leading-relaxed px-4 md:px-0">
              {t('why.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {WHY_ITEMS.map((item) => (
              <div
                key={item.title}
                className="p-5 md:p-6 bg-white border border-[#C2DFF2] hover:border-[#67CBFF] hover:shadow-md transition-all duration-300 group rounded-xl"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-[#DDEEFF] text-[#0E6AAF] group-hover:bg-[#0E6AAF] group-hover:text-white transition-all duration-300 mb-4 md:mb-5 rounded-lg">
                  <i className={`${item.icon} text-lg md:text-xl`} />
                </div>
                <h4 className="text-[#05294A] font-bold text-base mb-2">{item.title}</h4>
                <p className="text-[#2A6A96] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
